/**
 * Shared table search/filter primitives.
 *
 * Every list in the app describes itself with a `TableQuerySpec` and the toolbar
 * plus these helpers do the rest: text search, dropdown filters, date range,
 * amount range, sorting, paging and CSV export — either against the database
 * (server mode) or against rows already loaded (local mode).
 */

import { matchesUserSearch } from "@/components/admin/UserCell";

export interface TableFilterOption {
  value: string;
  label: string;
}

export interface TableFilterDef {
  /** Column (server mode) or property (local mode) this filter narrows. */
  key: string;
  label: string;
  options: TableFilterOption[];
  /** Match against several columns at once, e.g. internal_status || status. */
  columns?: string[];
  /** Local-only predicate for filters that aren't a plain column match. */
  predicate?: (row: any, value: string) => boolean;
}

export interface TableSortDef {
  key: string;
  label: string;
}

export interface TableExportColumn {
  key: string;
  label: string;
  value?: (row: any) => unknown;
}

export interface TableQuerySpec {
  /** Columns searched by the text box. */
  searchColumns: string[];
  /**
   * Columns holding an account id (e.g. `user_id`, `buyer_id`). The search box
   * resolves names/emails to account ids and matches them against these.
   */
  userColumns?: string[];
  /** Numeric columns matched exactly when the term parses as a number. */
  numericSearchColumns?: string[];
  filters?: TableFilterDef[];
  /** Column holding the record date; enables the date range control. */
  dateColumn?: string;
  /** Column holding the amount; enables the amount range control. */
  amountColumn?: string;
  sorts?: TableSortDef[];
  defaultSort?: { key: string; direction: "asc" | "desc" };
  exportColumns?: TableExportColumn[];
  exportFileName?: string;
  searchPlaceholder?: string;
}


export interface TableQueryState {
  search: string;
  filters: Record<string, string>;
  dateFrom: string;
  dateTo: string;
  amountMin: string;
  amountMax: string;
  sortKey: string;
  sortDirection: "asc" | "desc";
  page: number;
  pageSize: number;
}

export const EMPTY_TABLE_QUERY_STATE: TableQueryState = {
  search: "",
  filters: {},
  dateFrom: "",
  dateTo: "",
  amountMin: "",
  amountMax: "",
  sortKey: "",
  sortDirection: "desc",
  page: 1,
  pageSize: 20,
};

/** Keeps supabase-js from parsing select strings at the type level. */
export const sel = (s: string): string => s;

function escapeOrValue(value: string): string {
  // PostgREST `or=` lists are comma separated and parenthesised.
  return value.replace(/[,()]/g, " ").trim();
}

export function activeFilterCount(state: TableQueryState): number {
  let count = 0;
  if (state.search.trim()) count++;
  for (const v of Object.values(state.filters)) {
    if (v && v !== "all") count++;
  }
  if (state.dateFrom) count++;
  if (state.dateTo) count++;
  if (state.amountMin) count++;
  if (state.amountMax) count++;
  return count;
}

/**
 * Applies the toolbar state to a supabase query builder.
 * Pagination is applied only when `paginate` is true (CSV export walks pages
 * itself with its own range).
 */
export function applyServerQuery<Q>(
  query: Q,
  spec: TableQuerySpec,
  state: TableQueryState,
  options: { paginate?: boolean; userIds?: string[] } = {},
): Q {
  let q: any = query;

  const term = state.search.trim();
  const userIds = options.userIds ?? [];
  if (term) {
    const safe = escapeOrValue(term);
    const parts: string[] = [];
    if (safe) {
      for (const col of spec.searchColumns) parts.push(`${col}.ilike.%${safe}%`);
      const num = Number(safe);
      if (safe !== "" && !Number.isNaN(num)) {
        for (const col of spec.numericSearchColumns ?? []) parts.push(`${col}.eq.${num}`);
      }
    }
    if (userIds.length) {
      for (const col of spec.userColumns ?? ["user_id"]) parts.push(`${col}.in.(${userIds.join(",")})`);
    }
    if (parts.length) q = q.or(parts.join(","));
  }


  for (const def of spec.filters ?? []) {
    const value = state.filters[def.key];
    if (!value || value === "all") continue;
    const cols = def.columns ?? [def.key];
    if (cols.length === 1) {
      q = q.eq(cols[0], value);
    } else {
      q = q.or(cols.map((col) => `${col}.eq.${escapeOrValue(value)}`).join(","));
    }
  }

  if (spec.dateColumn) {
    if (state.dateFrom) q = q.gte(spec.dateColumn, new Date(`${state.dateFrom}T00:00:00.000Z`).toISOString());
    if (state.dateTo) q = q.lte(spec.dateColumn, new Date(`${state.dateTo}T23:59:59.999Z`).toISOString());
  }

  if (spec.amountColumn) {
    if (state.amountMin !== "" && !Number.isNaN(Number(state.amountMin))) {
      q = q.gte(spec.amountColumn, Number(state.amountMin));
    }
    if (state.amountMax !== "" && !Number.isNaN(Number(state.amountMax))) {
      q = q.lte(spec.amountColumn, Number(state.amountMax));
    }
  }

  const sortKey = state.sortKey || spec.defaultSort?.key || spec.dateColumn;
  if (sortKey) {
    q = q.order(sortKey, { ascending: state.sortDirection === "asc", nullsFirst: false });
  }

  if (options.paginate !== false) {
    const from = (Math.max(1, state.page) - 1) * state.pageSize;
    q = q.range(from, from + state.pageSize - 1);
  }

  return q as Q;
}

function pick(row: any, path: string): unknown {
  if (!path.includes(".")) return row?.[path];
  return path.split(".").reduce((acc: any, part) => (acc == null ? acc : acc[part]), row);
}

/** Local (already-loaded) filtering with the same semantics as the server path. */
export function applyTableFilters<T extends Record<string, any>>(
  rows: T[],
  spec: TableQuerySpec,
  state: TableQueryState,
): T[] {
  const term = state.search.trim().toLowerCase();
  const min = state.amountMin === "" ? null : Number(state.amountMin);
  const max = state.amountMax === "" ? null : Number(state.amountMax);
  const from = state.dateFrom ? new Date(`${state.dateFrom}T00:00:00`).getTime() : null;
  const to = state.dateTo ? new Date(`${state.dateTo}T23:59:59.999`).getTime() : null;

  const filtered = rows.filter((row) => {
    if (term) {
      const cols = spec.searchColumns;
      let hit = cols.some((col) => {
        const v = pick(row, col);
        return v != null && String(v).toLowerCase().includes(term);
      });
      if (!hit) {
        hit = (spec.userColumns ?? []).some((col) => {
          const id = pick(row, col);
          return typeof id === "string" && matchesUserSearch(id, term);
        });
      }
      if (!hit) return false;

    }

    for (const def of spec.filters ?? []) {
      const value = state.filters[def.key];
      if (!value || value === "all") continue;
      if (def.predicate) {
        if (!def.predicate(row, value)) return false;
        continue;
      }
      const cols = def.columns ?? [def.key];
      const hit = cols.some((col) => String(pick(row, col) ?? "") === value);
      if (!hit) return false;
    }

    if (spec.dateColumn && (from !== null || to !== null)) {
      const raw = pick(row, spec.dateColumn);
      const t = raw ? new Date(String(raw)).getTime() : NaN;
      if (Number.isNaN(t)) return false;
      if (from !== null && t < from) return false;
      if (to !== null && t > to) return false;
    }

    if (spec.amountColumn && (min !== null || max !== null)) {
      const amount = Number(pick(row, spec.amountColumn) ?? NaN);
      if (Number.isNaN(amount)) return false;
      if (min !== null && amount < min) return false;
      if (max !== null && amount > max) return false;
    }

    return true;
  });

  const sortKey = state.sortKey || spec.defaultSort?.key || spec.dateColumn;
  if (!sortKey) return filtered;

  const dir = state.sortDirection === "asc" ? 1 : -1;
  return [...filtered].sort((a, b) => {
    const av = pick(a, sortKey);
    const bv = pick(b, sortKey);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    const an = Number(av);
    const bn = Number(bv);
    if (!Number.isNaN(an) && !Number.isNaN(bn) && typeof av !== "boolean" && typeof bv !== "boolean") {
      return (an - bn) * dir;
    }
    const at = Date.parse(String(av));
    const bt = Date.parse(String(bv));
    if (!Number.isNaN(at) && !Number.isNaN(bt)) return (at - bt) * dir;
    return String(av).localeCompare(String(bv)) * dir;
  });
}

function csvCell(value: unknown): string {
  if (value == null) return "";
  const s = typeof value === "object" ? JSON.stringify(value) : String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function buildCsv(rows: any[], columns: TableExportColumn[]): string {
  const header = columns.map((c) => csvCell(c.label)).join(",");
  const body = rows.map((row) =>
    columns.map((c) => csvCell(c.value ? c.value(row) : pick(row, c.key))).join(","),
  );
  return [header, ...body].join("\n");
}

export function downloadCsv(fileName: string, csv: string): void {
  const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName.endsWith(".csv") ? fileName : `${fileName}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function defaultExportColumns(spec: TableQuerySpec): TableExportColumn[] {
  if (spec.exportColumns?.length) return spec.exportColumns;
  const keys = new Set<string>();
  if (spec.dateColumn) keys.add(spec.dateColumn);
  spec.searchColumns.forEach((k) => keys.add(k));
  (spec.filters ?? []).forEach((f) => (f.columns ?? [f.key]).forEach((k) => keys.add(k)));
  if (spec.amountColumn) keys.add(spec.amountColumn);
  return Array.from(keys).map((key) => ({ key, label: key }));
}
